import type { WorkSurface } from '@heidi/agents/contracts';
import {
  AGENT_CREDIT_ALLOWANCE_IN_CHAT_GAP_CLASS,
  agentCreditAllowanceBannerMessages,
  CreditAllowanceBannerNudge,
  creditAllowanceRefusalAtom,
  CreditLimitReachedUpgradeBanner,
  creditUsageUpgradeMultiplier,
  type AgentCreditAllowanceBannerPlacement,
  type AgentCreditAllowanceBannerSurface,
  type CreditUsagePopoverPlan,
} from '@heidi/commerce';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { getDefaultStore, useAtomValue } from 'jotai';
import { useIntl, type IntlShape } from 'react-intl';

import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import useReverseTrial from '@/hooks/useReverseTrial';

import {
  useAgentCreditAllowanceBannerState,
  useTrackQuotaUpgradeViewed,
} from './useAgentCreditAllowanceBannerState';

const BANNER_TEST_ID = 'agent-credit-allowance-banner';

function formatResetParts(intl: IntlShape, cycleEnd: string) {
  const resetAt = new Date(cycleEnd);
  return {
    date: intl.formatDate(resetAt, { month: 'short', day: 'numeric' }),
    time: intl.formatDate(resetAt, { hour: 'numeric', minute: '2-digit' }),
  };
}

export type AgentCreditAllowanceBannerViewProps = {
  surface: AgentCreditAllowanceBannerSurface;
  /** Max plan the banner sells; Practice seats get Practice Max copy and tone. */
  plan?: CreditUsagePopoverPlan;
  hideCTA?: boolean;
  cycleEnd?: string | null;
  isFreeUser?: boolean;
  onDismiss?: () => void;
  onUpgradeClick?: () => void;
  className?: string;
};

/** Forced-surface render — live hook and `/dev` fixtures both drive this. */
export function AgentCreditAllowanceBannerView({
  surface,
  plan = 'clinicianMax',
  hideCTA = false,
  cycleEnd,
  isFreeUser = false,
  onDismiss,
  onUpgradeClick,
  className,
}: AgentCreditAllowanceBannerViewProps) {
  const intl = useIntl();
  const usageMultiplier = creditUsageUpgradeMultiplier(isFreeUser);

  if (surface.kind === 'complimentary') {
    return (
      <CreditLimitReachedUpgradeBanner
        plan={plan}
        threshold={100}
        hideCTA
        dismissible
        onDismiss={onDismiss}
        className={className}
        data-testid={BANNER_TEST_ID}
        copy={{
          title: agentCreditAllowanceBannerMessages.complimentaryTitle,
          description: agentCreditAllowanceBannerMessages.complimentaryDescription,
        }}
      />
    );
  }

  const resetParts = cycleEnd != null ? formatResetParts(intl, cycleEnd) : null;
  const exhaustedCopy =
    surface.threshold === 100
      ? {
          title: agentCreditAllowanceBannerMessages.exhaustedTitle,
          ...(resetParts != null
            ? {
                description: intl.formatMessage(
                  plan === 'practiceMax'
                    ? agentCreditAllowanceBannerMessages.exhaustedDescriptionPracticeMax
                    : agentCreditAllowanceBannerMessages.exhaustedDescription,
                  { ...resetParts, multiplier: usageMultiplier }
                ),
              }
            : {}),
        }
      : undefined;

  return (
    <CreditLimitReachedUpgradeBanner
      plan={plan}
      threshold={surface.threshold}
      isFreeUser={isFreeUser}
      hideCTA={hideCTA}
      dismissible={surface.dismissible}
      onDismiss={surface.dismissible ? onDismiss : undefined}
      onUpgradeClick={onUpgradeClick}
      copy={exhaustedCopy}
      className={className}
      data-testid={BANNER_TEST_ID}
    />
  );
}

type AgentCreditAllowanceBannerProps = {
  className?: string;
  /** Where the banner renders, for work_upgrade_actioned. */
  analyticsSurface?: WorkSurface;
};

/** Account-level credit nudge — app-owned wrapper over the Growth banner. */
export function AgentCreditAllowanceBanner({
  className,
  analyticsSurface = 'project_chat',
}: AgentCreditAllowanceBannerProps) {
  const { userTier } = useQueryUserTier();
  const { isClinicianTrialUser } = useReverseTrial();
  const isFreeUser = userTier === SubscriptionTier.FREE || isClinicianTrialUser;
  const { surface, plan, hideCTA, cycleEnd, onDismiss, onUpgradeClick } =
    useAgentCreditAllowanceBannerState(analyticsSurface);
  useTrackQuotaUpgradeViewed(surface, analyticsSurface, plan);

  if (surface == null) {
    return null;
  }

  return (
    <AgentCreditAllowanceBannerView
      surface={surface}
      plan={plan}
      hideCTA={hideCTA}
      cycleEnd={cycleEnd}
      isFreeUser={isFreeUser}
      onDismiss={onDismiss}
      onUpgradeClick={onUpgradeClick}
      className={className}
    />
  );
}

/** Composer dock keeps the 16px gap; transcript placement uses the timeline gap. */
export function AgentCreditAllowanceInChatBanner({
  placement = 'composer',
}: {
  placement?: AgentCreditAllowanceBannerPlacement;
}) {
  const refusal = useAtomValue(creditAllowanceRefusalAtom, {
    store: getDefaultStore(),
  });
  return (
    <CreditAllowanceBannerNudge nudgeNonce={refusal?.nudgeNonce ?? 0}>
      <AgentCreditAllowanceBanner
        className={
          placement === 'composer' ? AGENT_CREDIT_ALLOWANCE_IN_CHAT_GAP_CLASS : undefined
        }
      />
    </CreditAllowanceBannerNudge>
  );
}
